/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Field, useFormikContext } from 'formik';
import * as Yup from 'yup';

import {
  INPUT_ELEMENTS,
  TEXT_ELEMENTS,
  OTHER_ELEMENTS,
  TEXT_TYPES,
  INPUT_TYPES,
  ELEMENT_TYPES,
} from '../../constants/formElements';
import {
  InputsSchema,
  DynamicInputFieldSchema,
  ROLE,
} from './validationSchema';
import CustomValidationFunc from './validationsFunc';
import 'datejs';
import VALIDATION_RULES_NAME from '../../constants/validationRulesName';
import {
  getTranslation,
  isKeyExist,
  getFormconfigElements,
} from '../../utils/functions';
import {
  formInitialValuesSelector,
  languageSelector,
  validationErrorsSelector,
} from '../../store/selectors/appSelectors';
import {
  currentStepSelector,
  registerDataSelector,
  stepsSelector,
} from '../../store/selectors/formConfigSelectors';
import { FIELDS_NAME } from '../../constants/fields';
import store from '../../store';

import { COUNTRY_CODE } from '../../constants/countryList';
import { getCountriesWithAllowedStates } from '../../services/accreditation-forms-service';

const getDateDefaultValue = (defaultValue, notEarlierThan, notLaterThan) => {
  const todayDate = Date.today().toString('yyyy-MM-dd');

  if (defaultValue === null || defaultValue === '') {
    return null;
  }

  if (typeof defaultValue === 'undefined') {
    if (!notLaterThan) return todayDate;
    const isTodayCorrect =
      Date.compare(Date.parse(todayDate), Date.parse(notLaterThan)) <= 0;
    return isTodayCorrect ? todayDate : notLaterThan;
  }

  const isDateBiggerThanMin = notEarlierThan
    ? Date.compare(Date.parse(defaultValue), Date.parse(notEarlierThan)) >= 0
    : true;

  const isDateLessThanMax = notLaterThan
    ? Date.compare(Date.parse(defaultValue), Date.parse(notLaterThan)) <= 0
    : true;

  const isDefaultDateValid = isDateBiggerThanMin && isDateLessThanMax;
  return isDefaultDateValid ? defaultValue : notLaterThan || defaultValue;
};

export const BuildForm = (props) => {
  const { currentFormConfig, handleValidation } = props || {};
  const federalStatesElement = getFormconfigElements(
    currentFormConfig,
    FIELDS_NAME.FEDERAL_STATES,
  );
  const addressState = getFormconfigElements(
    currentFormConfig,
    FIELDS_NAME.STATE,
  );
  const jobTitleFormConfig = getFormconfigElements(
    currentFormConfig,
    FIELDS_NAME.JOB_TITLE,
  );
  const previousNamesFormConfig = getFormconfigElements(
    currentFormConfig,
    FIELDS_NAME.PREVIOUS_NAMES,
  );
  const departmentElement = getFormconfigElements(
    currentFormConfig,
    FIELDS_NAME.DEPARTMENT,
  );

  const { title, sections } = currentFormConfig || {};

  const [isGermanResident, setIsGermanResident] = useState(false);
  const [countryWithAllowedStatesList, setCountryWithAllowedStatesList] =
    useState([]);

  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    let federalStateValidation;
    if (
      federalStatesElement &&
      DynamicInputFieldSchema[FIELDS_NAME.FEDERAL_STATES]
    ) {
      federalStateValidation = {
        [FIELDS_NAME.FEDERAL_STATES]:
          DynamicInputFieldSchema[FIELDS_NAME.FEDERAL_STATES](
            federalStatesElement,
          ),
      };
    }
    if (isGermanResident && federalStateValidation) {
      handleValidation(federalStateValidation);
    }
  }, [isGermanResident]);

  useEffect(() => {
    let validations;
    if (
      previousNamesFormConfig &&
      DynamicInputFieldSchema[FIELDS_NAME.PREVIOUS_NAMES]
    ) {
      validations = {
        [FIELDS_NAME.PREVIOUS_NAMES]: DynamicInputFieldSchema[
          FIELDS_NAME.PREVIOUS_NAMES
        ](previousNamesFormConfig),
      };
    }
    if (values?.countryOfBirth && validations) {
      handleValidation(validations);
    }
  }, [values?.countryOfBirth]);

  useEffect(() => {
    if (departmentElement && departmentElement?.properties?.autoUppercase) {
      setFieldValue('department', values?.department?.toUpperCase());
    }
  }, [values?.department]);

  useEffect(() => {
    getCountryWithAllowedStatesList();
  }, []);

  const getCountryWithAllowedStatesList = async () => {
    const { tenantId } = store.getState().formSession;

    const countries = await getCountriesWithAllowedStates(tenantId);
    if (countries) {
      setCountryWithAllowedStatesList(countries);
    }
  };

  useEffect(() => {
    let jobTitleValidation;
    if (jobTitleFormConfig && DynamicInputFieldSchema[FIELDS_NAME.JOB_TITLE]) {
      jobTitleValidation = {
        [FIELDS_NAME.JOB_TITLE]:
          DynamicInputFieldSchema[FIELDS_NAME.JOB_TITLE](jobTitleFormConfig),
      };
    }
    if (values?.title && jobTitleValidation) {
      handleValidation(jobTitleValidation);
    }
  }, [values?.title]);

  useEffect(() => {
    let addressStateValidation;

    if (addressState && DynamicInputFieldSchema[FIELDS_NAME.STATE]) {
      addressStateValidation = {
        [FIELDS_NAME.STATE]:
          DynamicInputFieldSchema[FIELDS_NAME.STATE](addressState),
      };
    }

    if (
      values?.addressCountry === COUNTRY_CODE.GERMANY &&
      addressStateValidation
    ) {
      handleValidation(addressStateValidation);
    }
  }, [values?.addressCountry]);

  const regForm = [];
  const stepTitleElement = (
    <TEXT_ELEMENTS.SECTION_TEXT
      key="step-title"
      text={title}
      type={TEXT_TYPES.SECTION_TEXT}
    />
  );

  regForm.push(stepTitleElement);

  (sections ?? []).forEach((section, sectionIndex) => {
    const sectionTitleElement = (
      <TEXT_ELEMENTS.SECTION_HEADER
        key={`header-${sectionIndex}`}
        text={section.title}
        type={TEXT_TYPES.SECTION_HEADER}
      />
    );
    const GENERAL_PROPERTIES = (elem, prop) => {
      let required = elem.required;
      if (elem.dependsOnFieldCode) {
        required = !!values[elem.dependsOnFieldCode] ? elem.required : false;
      }
      return {
        key: elem.fieldCode,
        component: INPUT_ELEMENTS[elem.typeCode],
        name: elem.fieldCode,
        required: required,
        hint: isKeyExist(prop.hint),
        text: isKeyExist(prop.text),
        label: isKeyExist(prop.label),
        autoUppercase: !!elem?.properties?.autoUppercase,
        validate: CustomValidationFunc[elem.typeCode]?.(elem),
      };
    };

    const READ_ONLY_TEXT_PROPERTIES = (prop) => {
      return {
        defaultText: isKeyExist(prop.label),
      };
    };

    const FEDERAL_STATE_PROPERTIES = () => {
      return {
        required: isGermanResident || values.nativeResident,
      };
    };

    const PREVIOUS_NAMES_PROPERTIES = () => {
      return {
        required: values.countryOfBirth === COUNTRY_CODE.ITALY,
      };
    };

    // Jobtitle label is organization
    const JOB_TITLE_PROPERTIES = () => {
      return {
        required: values?.title && values?.title !== ROLE.RESIDENT,
      };
    };

    const DATE_PICKER_PROPERTIES = (prop) => {
      return {
        notearlierthan: isKeyExist(prop.notEarlierThan, false),
        notlaterthan: isKeyExist(prop.notLaterThan, false),
        errormessagenotearlierthan: isKeyExist(prop.errorMessageNotEarlierThan),
        errormessagenotlaterthan: isKeyExist(prop.errorMessageNotLaterThan),
        format: prop.format,
      };
    };

    const FILE_UPLOAD_SINGLE_PROPERTIES = (prop) => {
      return {
        extensiontype: prop.acceptedFileExtensions,
        description: isKeyExist(prop.description),
        errormessagerequired: isKeyExist(prop.errorMessageRequired),
      };
    };

    const handleChange = (event, val) => {
      setIsGermanResident(val);
    };

    const DROPDOWN_PROPERTIES = (prop, typeCode) => {
      if (typeCode === INPUT_TYPES.NATIVE_RESIDENT_SELECT) {
        return {
          options: prop.values ?? [],
          handleChange: (event, val) => handleChange(event, val),
        };
      }

      return {
        useApiForOptions: prop.useApiForOptions,
        apiEndpoint: prop.apiEndpoint,
        options: prop.values ?? [],
        isMultiSelect: prop.isMultiSelect ?? false,
      };
    };

    const sectionElements = (section?.elements ?? []).map(
      (element, elementIndex) => {
        if (element.typeCode === ELEMENT_TYPES.SAMPLE_PHOTOS) {
          return (
            <OTHER_ELEMENTS.SAMPLE_PHOTOS
              key={`samplePhotos-${elementIndex}`}
              wrongSamples={element.properties?.wrongSamples ?? []}
              correctSamples={element.properties?.correctSamples ?? []}
            />
          );
        }

        if (element.typeCode === ELEMENT_TYPES.BULLET_LIST) {
          return (
            <OTHER_ELEMENTS.BULLET_LIST
              key={`bulletList-${elementIndex}`}
              bullets={element.properties?.content ?? []}
            />
          );
        }

        if (
          element.typeCode === INPUT_TYPES.STATE_SELECT &&
          !countryWithAllowedStatesList.includes(values?.addressCountry) &&
          !countryWithAllowedStatesList.includes(values?.countryOfBirth)
        ) {
          return null;
        }

        if (
          element.typeCode === INPUT_TYPES.STATE_MULTI_SELECT &&
          !isGermanResident &&
          !values.nativeResident
        ) {
          return null;
        }

        // if (element.typeCode === INPUT_TYPES.STATE_MULTI_SELECT && !selectedCountry) {
        //   return null;
        // }
        if (element.typeCode === INPUT_TYPES.READ_ONLY_TEXT) {
          const Comp = INPUT_ELEMENTS[element.typeCode];
          return <Comp {...READ_ONLY_TEXT_PROPERTIES(element.properties)} />;
        }
        if (element.fieldCode) {
          const elem = element;
          const prop = element.properties;

          return (
            <Field
              {...GENERAL_PROPERTIES(elem, prop)}
              {...DATE_PICKER_PROPERTIES(prop)}
              {...FILE_UPLOAD_SINGLE_PROPERTIES(prop)}
              {...DROPDOWN_PROPERTIES(prop, element.typeCode)}
              {...(element.typeCode === INPUT_TYPES.STATE_MULTI_SELECT && {
                ...FEDERAL_STATE_PROPERTIES(),
              })}
              {...(element.fieldCode === FIELDS_NAME.JOB_TITLE && {
                ...JOB_TITLE_PROPERTIES(),
              })}
              {...(element.fieldCode === FIELDS_NAME.PREVIOUS_NAMES && {
                ...PREVIOUS_NAMES_PROPERTIES(),
              })}
            />
          );
        }
        return (
          <TEXT_ELEMENTS.SUBSECTION_TEXT
            key={`subsectionText-${elementIndex}`}
            text={element.properties.text}
            type={TEXT_TYPES.SUBSECTION_TEXT}
          />
        );
      },
    );
    const dividerElement = (
      <OTHER_ELEMENTS.DIVIDER
        key={`divider-${sectionIndex}`}
        style={{ marginBottom: 40, width: '100%' }}
      />
    );

    if (
      section.title &&
      section.dependsOnFieldCode &&
      !values[section.dependsOnFieldCode]
    ) {
      return;
    }
    if (section.title) {
      regForm.push(sectionTitleElement);
    }
    regForm.push(...sectionElements);
    regForm.push(dividerElement);
  });

  return <div>{regForm}</div>;
};

const buildInitialValues = ({
  currentFormConfig: { sections },
  previousFillData,
  formInitialValues,
  formikValues,
}) => {
  const initialValues = {};

  (sections ?? []).forEach(({ elements }) =>
    (elements ?? []).forEach(
      ({
        fieldCode,
        properties: { defaultValue, notEarlierThan, notLaterThan },
        typeCode,
        isCustomField,
      }) => {
        if (fieldCode) {
          if (
            formikValues &&
            typeof formikValues?.[fieldCode] !== 'undefined'
          ) {
            initialValues[fieldCode] = formikValues[fieldCode];
            return;
          }

          if (
            isCustomField &&
            typeof previousFillData?.customFields?.[fieldCode] !== 'undefined'
          ) {
            initialValues[fieldCode] =
              formInitialValues[fieldCode] ?? previousFillData[fieldCode];
          }

          if (typeof formInitialValues[fieldCode] !== 'undefined') {
            initialValues[fieldCode] = formInitialValues[fieldCode];
            return;
          }

          if (typeof previousFillData?.[fieldCode] !== 'undefined') {
            initialValues[fieldCode] = previousFillData[fieldCode];
            return;
          }

          if (typeCode === INPUT_TYPES.DATE_PICKER) {
            initialValues[fieldCode] = getDateDefaultValue(
              defaultValue,
              notEarlierThan,
              notLaterThan,
            );
            return;
          }

          if (typeof defaultValue !== 'undefined') {
            initialValues[fieldCode] = defaultValue;
            return;
          }
          initialValues[fieldCode] = '';
        }
      },
    ),
  );

  return initialValues;
};

const buildValidationSchema = ({ sections }) => {
  const validationSchema = {};
  (sections ?? []).forEach((section) =>
    (section.elements ?? []).forEach((element) => {
      if (InputsSchema[element.typeCode]) {
        let schema = InputsSchema[element.typeCode](element);
        if (element.dependsOnFieldCode) {
          schema = Yup.mixed().when(element.dependsOnFieldCode, {
            is: true,
            then: schema,
            otherwise: Yup.mixed().notRequired(),
          });
        }
        validationSchema[element.fieldCode] = schema;
      }
    }),
  );
  return validationSchema;
};

const buildInitialTouched = (initialErrors, initialValues) => {
  if (initialErrors.length === 0) return {};
  return Object.keys(initialValues).reduce(
    (res, fieldCode) => ({ ...res, [fieldCode]: true }),
    {},
  );
};

const buildInitialErrors = (errors, { sections }) => {
  let elementsProperties = {};

  if (errors.length === 0) return {};

  (sections ?? []).forEach(({ elements }) => {
    elementsProperties = {
      ...elementsProperties,
      ...elements.reduce((res, { fieldCode, properties }) => {
        if (!fieldCode) return res;
        return {
          ...res,
          [fieldCode]: {
            ...properties,
          },
        };
      }, {}),
    };
  });

  const initialErrors = errors
    .reverse()
    .reduce((res, { fieldCode, ruleName, message: defaultMessage }) => {
      const element = elementsProperties[fieldCode];
      if (element) {
        const errorName = VALIDATION_RULES_NAME[ruleName];
        const errorMessage = element[errorName];
        return {
          ...res,
          [fieldCode]: defaultMessage || getTranslation(errorMessage),
        };
      }

      return res;
    }, {});

  return initialErrors;
};

const useBuildRegisterForm = (formikValues) => {
  const {
    formInitialValues,
    validationErrors,
    language,
    currentStep,
    steps,
    registerData,
    // selectedCountry,
  } = useSelector((state) => ({
    // app state
    formInitialValues: formInitialValuesSelector(state),
    validationErrors: validationErrorsSelector(state),
    language: languageSelector(state),
    // formConfig state
    currentStep: currentStepSelector(state),
    steps: stepsSelector(state),
    registerData: registerDataSelector(state),
    // selectedCountry: countrySelector(state),
  }));

  const [initialValues, setInitialValues] = useState({});
  const [validationSchema, setValidationSchema] = useState({});
  const [isFormBuilding, setFormBuilding] = useState(true);
  const [initialTouched, setInitialTouched] = useState({});
  const [initialErrors, setInitialErrors] = useState({});
  const [currentConf, setCurrentConf] = useState();

  useEffect(() => {
    setFormBuilding(true);
    if (currentStep === steps.length) return;

    const currentFormConfig = steps[currentStep];
    setCurrentConf(currentFormConfig);
    const previousFillData = registerData[currentStep];

    const newInitialValues = buildInitialValues({
      currentFormConfig,
      previousFillData,
      formInitialValues,
      formikValues,
    });

    setInitialValues(newInitialValues);
    setInitialTouched(
      buildInitialTouched(validationErrors.errors, newInitialValues),
    );
    setInitialErrors(
      buildInitialErrors(validationErrors.errors, currentFormConfig),
    );
    setValidationSchema(buildValidationSchema(currentFormConfig));

    setTimeout(() => {
      setFormBuilding(false);
    }, 0);
  }, [currentStep, steps, validationErrors, language, formInitialValues]);

  return {
    currentConf,
    initialValues,
    validationSchema,
    isFormBuilding,
    initialTouched,
    initialErrors,
  };
};

export default useBuildRegisterForm;
